import { noop, emptyObject, stringReturnNoop, STASH_ITEMS_DELIMETER, EXPERIMENTS_VIEWED_USER_KEY, EXPERIMENTS_VIEWED_STASH_KEY, REASON_RULE_MATCH, VARIANT_EXCLUDE, REASON_FALLTHROUGH, EVALUATION_NO_EVALUATIONS, EVALUATION_NOT_FOUND, HTJS_USER_ID, ERROR_BOUNDARY_LOG_MSG } from '@shutterstock/experiments-constants';
import { createContext, useContext, useRef, useCallback, useState, useEffect, useMemo, Component } from 'react';
import { jsx } from 'react/jsx-runtime';
import { getEvaluationsWithOverrides } from '@shutterstock/experiments-api-client';

var DEFAULT_DEBUG_MODE = false;
/** Whether to use persist items in stash (local storage or cookies.
 * Can have performance implications since writing to stash is blocking atm.
 * Ideally default would be true, but started sdk without feature and want to seamlessly maintain support on version bumps */
var DEFAULT_DO_EXPERIMENTS_VIEWED_STASH = false;
var DEFAULT_APP_NAME = 'n/a';
var DEFAULT_CONFIGURATION = {
  doEmptyProviderLogging: false,
  debugMode: DEFAULT_DEBUG_MODE,
  doExperimentStashDeduplication: DEFAULT_DO_EXPERIMENTS_VIEWED_STASH,
  appName: DEFAULT_APP_NAME
};

var ExperimentsContext = createContext({
  analytics: {
    experimentViewed: noop
  },
  config: DEFAULT_CONFIGURATION,
  evaluations: emptyObject,
  logger: {
    debug: stringReturnNoop,
    error: stringReturnNoop
  },
  setEvaluations: stringReturnNoop,
  user: emptyObject,
  renderErrors: {
    current: emptyObject
  }
});
ExperimentsContext.displayName = 'ExperimentsContext';
var useExperimentsContext = function useExperimentsContext() {
  return useContext(ExperimentsContext);
};

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;
  _setPrototypeOf(subClass, superClass);
}
function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };
  return _setPrototypeOf(o, p);
}
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}

var ExperimentsViewedContext = createContext({
  setExperimentViewed: noop,
  experimentsViewedRef: {
    current: emptyObject
  }
});
var useExperimentsViewedContext = function useExperimentsViewedContext() {
  return useContext(ExperimentsViewedContext);
};

var isBrowser = function isBrowser() {
  return typeof window !== 'undefined';
};
var NewRelicNoticeError = function NewRelicNoticeError(msg, ctx) {
  var _window;
  if (isBrowser() && (_window = window) != null && _window.NREUM && window.NREUM.noticeError) {
    var newRelicError = new Error(msg + " | More details in \"attributes\".");
    newRelicError.name = 'ExperimentsError';
    var rawError = ctx.error ? ctx.error.toString() : 'falsy error provided';
    window.NREUM.noticeError(newRelicError, _extends({}, ctx.customAttributes, window.location, {
      referrerPath: window.location.pathname,
      referrerQuery: window.location.search,
      navigator: window.navigator.userAgent,
      rawError: rawError
    }));
  }
};
var debug = function debug(msg, ctx) {
  if (isBrowser()) {
    console.log('EXPERIMENT DEBUG =============================');
    console.log(msg, ctx);
  }
};
var setCustomAttribute = function setCustomAttribute(_ref) {
  var _window2;
  var name = _ref.name,
    value = _ref.value,
    _ref$persist = _ref.persist,
    persist = _ref$persist === void 0 ? false : _ref$persist;
  if (isBrowser() && ((_window2 = window) == null || (_window2 = _window2.NREUM) == null ? void 0 : _window2.setCustomAttribute) !== undefined) {
    window.NREUM.setCustomAttribute(name, value, persist);
  }
};
var addPageAction = function addPageAction(_ref2) {
  var name = _ref2.name,
    data = _ref2.data;
  if (isBrowser()) {
    var _window$NREUM;
    (_window$NREUM = window.NREUM) == null || _window$NREUM.addPageAction(name, data);
  }
};
var getLogger = function getLogger(debugMode) {
  return {
    debug: debugMode ? debug : function () {
      return '';
    },
    error: function error(msg, ctx) {
      NewRelicNoticeError(msg, ctx);
      this.debug(msg, ctx);
    }
  };
};

// return cookie value or empty string
var getCookie = function getCookie(key) {
  var cookieValue = '';
  try {
    var cookies = document.cookie ? document.cookie.split('; ') : [];
    for (var i = 0; i < cookies.length; i++) {
      var _cookies$i$split = cookies[i].split('='),
        k = _cookies$i$split[0],
        v = _cookies$i$split[1];
      try {
        var cookieKey = decodeURIComponent(k);
        if (cookieKey === key) {
          cookieValue = decodeURIComponent(v);
          break;
        }
      } catch (e) {
        /* empty */
      }
    }
  } catch (e) {
    /* empty */
  } // TODO log to NR?
  return cookieValue;
};

// TODO delete & replace with experiments-sdk-next Stash.ts, but for now want to limit scope of already big PR
var IS_BROWSER = typeof window !== 'undefined';
var isStorageSupported = function isStorageSupported() {
  var testKey = 'test';
  try {
    localStorage.setItem(testKey, 'ok');
    localStorage.removeItem(testKey);
    return true;
  } catch (e) {
  }
  return false;
};
var getCookieAccessors = function getCookieAccessors(key, expires) {
  return {
    get value() {
      return getCookie(key);
    },
    set: function set(val) {
      try {
        var cookieKey = encodeURIComponent(key);
        var cookieValue = encodeURIComponent(val);
        var expiresValue = new Date(Date.now() + expires * 864e5).toUTCString();
        document.cookie = cookieKey + "=" + cookieValue + "; expires=" + expiresValue;
      } catch (e) {
        // TODO log to NR?
        return;
      }
    }
  };
};
var Stash = function Stash(key) {
  var accessors = isStorageSupported() ? {
    get value() {
      return localStorage.getItem(key) || '';
    },
    set: function set(val) {
      localStorage.setItem(key, val);
    }
  } : getCookieAccessors(key, 60);
  var getValue = function getValue(errorCallback) {
    if (IS_BROWSER) {
      try {
        return accessors.value;
      } catch (e) {
        if (errorCallback !== undefined) {
          errorCallback(e);
        }
        return '';
      }
    }
    return '';
  };
  var setValue = function setValue(val, errorCallback) {
    if (IS_BROWSER) {
      try {
        return accessors.set(val);
      } catch (e) {
        if (errorCallback !== undefined) {
          errorCallback(e);
        }
        return;
      } // TODO log in NR?
    }
  };
  var getJsonValue = function getJsonValue(errorCallback) {
    var errorCb = errorCallback !== undefined ? errorCallback : function (error) {
      return console.error(key + " getJsonValue: err ", error);
    };
    // First get from stash
    try {
      var value = getValue();
      // expected when no existing entry, but will error on parse
      if (value.length === 0) {
        return {};
      }
      return JSON.parse(value);
    } catch (error) {
      errorCb(error);
      return {};
    }
  };
  return {
    value: getValue(),
    getJsonValue: getJsonValue,
    getItems: function getItems() {
      return getValue().split(STASH_ITEMS_DELIMETER);
    },
    setItems: function setItems(items, errorCallback) {
      var itemsStr = Array.from(new Set(items)).join(STASH_ITEMS_DELIMETER);
      setValue(itemsStr, errorCallback);
      this.value = getValue();
    },
    clear: function clear() {
      localStorage.removeItem(key);
    }
  };
};

var userBucketStash = Stash(EXPERIMENTS_VIEWED_USER_KEY);
var experimentsViewedStash = Stash(EXPERIMENTS_VIEWED_STASH_KEY);

var getErrorData = function getErrorData(error) {
  var eError = error;
  return {
    errorMessage: eError == null ? void 0 : eError.message,
    errorName: eError == null ? void 0 : eError.name,
    errorStack: eError == null ? void 0 : eError.stack
  };
};
var getParsedJsonValue = function getParsedJsonValue(stashKey, stash, debugMode) {
  var errorCallback = function errorCallback(error) {
    var data = _extends({
      val: stash.getItems().toString(),
      stashKey: stashKey
    }, getErrorData(error));
    var errorName = "ExperimentViewedStashGetError";
    if (debugMode) {
      console.info(errorName, data);
    }
    addPageAction({
      name: errorName,
      data: data
    });
  };
  return stash.getJsonValue(errorCallback);
};
var setStashItems = function setStashItems(stashKey, val, stash, debugMode) {
  var errorCallback = function errorCallback(error) {
    var data = _extends({
      stashKey: stashKey,
      val: val
    }, getErrorData(error));
    // TODO link relevant NR query for monitoring
    // Will link to the NR query
    var errorName = "ExperimentViewedStashSetError";
    if (debugMode) {
      console.info(errorName, data);
    }
    addPageAction({
      name: errorName,
      data: data
    });
  };
  stash.setItems([val], errorCallback);
};

var getSetExperimentViewedFn = function getSetExperimentViewedFn(_ref) {
  var setExperimentsViewedRef = _ref.setExperimentsViewedRef,
    experimentsViewedRef = _ref.experimentsViewedRef,
    analytics = _ref.analytics,
    config = _ref.config;
  var doExperimentStashDeduplication = config.doExperimentStashDeduplication,
    appName = config.appName,
    debugMode = config.debugMode;
  var logger = getLogger(config.debugMode);
  var setExperimentViewed = function setExperimentViewed(_ref2) {
    var experimentId = _ref2.experimentId,
      evaluation = _ref2.evaluation,
      exclude = _ref2.exclude;
    var variationId = evaluation.variationId,
      reason = evaluation.reason;
    if (reason !== REASON_RULE_MATCH) {
      return;
    }
    var experimentsViewed = experimentsViewedRef.current;
    // Handle new impression recording case
    if ((experimentsViewed == null ? void 0 : experimentsViewed[experimentId]) === undefined) {
      var _extends2;
      var viewedVariation = exclude ? VARIANT_EXCLUDE : "" + variationId;
      var newExperimentsViewed = _extends({}, experimentsViewed, (_extends2 = {}, _extends2[experimentId] = viewedVariation, _extends2));
      setExperimentsViewedRef(newExperimentsViewed);
      // Thread blocking potential source of increased TBT load times on mobile
      // TODO investigate speedup options
      if (doExperimentStashDeduplication) {
        setStashItems(EXPERIMENTS_VIEWED_STASH_KEY, JSON.stringify(newExperimentsViewed), experimentsViewedStash, debugMode);
      }
      analytics.experimentViewed({
        experiment_id: experimentId,
        source: appName,
        variation_id: viewedVariation
      });
      return;
    }
    var previousVariation = experimentsViewed[experimentId];
    if (previousVariation !== "" + variationId) {
      logger.error(
      // This state should be impossible
      // Check for user bucketing info has been consistent, or for rule changes.
      'setExperimentViewed detected mismatch variation.', {
        customAttributes: {
          experimentId: experimentId,
          previousVariation: previousVariation,
          variationId: variationId,
          reason: reason
        }
      });
      return;
    }
    return;
  };
  return setExperimentViewed;
};

var useGetExperimentViewedContext = function useGetExperimentViewedContext(_ref) {
  var analytics = _ref.analytics,
    config = _ref.config,
    initialExperimentsViewed = _ref.experimentsViewed;
  // useRef because we want to update the ExperimentsViewed without triggering any re-render
  // tracking impression recording never affect rendering logic.
  var experimentsViewedRef = useRef(initialExperimentsViewed);
  // Function to update the ref value
  var setExperimentsViewedRef = useCallback(function (newState) {
    experimentsViewedRef.current = newState;
  }, []);
  // Utility fn to update ref & localStorage
  var setExperimentViewed = getSetExperimentViewedFn({
    setExperimentsViewedRef: setExperimentsViewedRef,
    experimentsViewedRef: experimentsViewedRef,
    analytics: analytics,
    config: config
  });
  return {
    setExperimentViewed: setExperimentViewed,
    experimentsViewedRef: experimentsViewedRef
  };
};

var ExperimentsViewedProvider = function ExperimentsViewedProvider(_ref) {
  var analytics = _ref.analytics,
    config = _ref.config,
    experimentsViewed = _ref.experimentsViewed,
    children = _ref.children;
  var _useGetExperimentView = useGetExperimentViewedContext({
      analytics: analytics,
      config: config,
      experimentsViewed: experimentsViewed
    }),
    setExperimentViewed = _useGetExperimentView.setExperimentViewed,
    experimentsViewedRef = _useGetExperimentView.experimentsViewedRef;
  return jsx(ExperimentsViewedContext.Provider, {
    value: {
      setExperimentViewed: setExperimentViewed,
      experimentsViewedRef: experimentsViewedRef
    },
    children: children
  });
};

/*** ***
 * Fetches experiments viewed for those which are in the stash & in the serverEvaluations
 * This function has the side effects from & on stash (cookie or localStorage)
 * Reads & sets the `EXPERIMENTS_VIEWED_STASH_KEY` key in stash
 * Reads & sets the `EXPERIMENTS_VIEWED_USER_KEY` key in stash
 */
var getInitialExperimentsViewed = function getInitialExperimentsViewed(_ref) {
  var user = _ref.user,
    serverEvaluations = _ref.serverEvaluations,
    debugMode = _ref.debugMode;
  var stashUser = getParsedJsonValue(EXPERIMENTS_VIEWED_USER_KEY, userBucketStash, debugMode);
  // If user identifying user information changes, we'll want to re-send expeirment impression events
  // ex: multiple UserId accounts.
  if (stashUser.anonymousId !== user.anonymousId || stashUser.userId !== user.userId) {
    setStashItems(EXPERIMENTS_VIEWED_USER_KEY, JSON.stringify({
      anonymousId: user.anonymousId,
      userId: user.userId
    }), userBucketStash, debugMode);
    // Reset experiments viewed as users should re-record changes
    setStashItems(EXPERIMENTS_VIEWED_STASH_KEY, JSON.stringify({}), experimentsViewedStash, debugMode);
    return {};
  }
  var stashedExperimentsViewed = getParsedJsonValue(EXPERIMENTS_VIEWED_STASH_KEY, experimentsViewedStash, debugMode);
  var trimmedExperimentsViewed = stashedExperimentsViewed;
  // Then align stash to server
  Object.keys(trimmedExperimentsViewed).forEach(function (key) {
    var serverEval = serverEvaluations[key];
    if (serverEval === undefined ||
    // Erase fallthrough reasons from stash so completed experiments can be trimmed
    // prevents stash from growing until it breaks
    serverEval.reason === REASON_FALLTHROUGH ||
    // Delete record of variation tracking unless it was exclude
    // We do this so we can record impression for it,
    // Then analytics can detect data pollution
    trimmedExperimentsViewed[key] !== "" + serverEval.variationId && stashedExperimentsViewed[key] !== VARIANT_EXCLUDE) {
      delete trimmedExperimentsViewed[key];
    }
  });
  setStashItems(EXPERIMENTS_VIEWED_STASH_KEY, JSON.stringify(trimmedExperimentsViewed), experimentsViewedStash, debugMode);
  return stashedExperimentsViewed;
};

var ExperimentsProvider = function ExperimentsProvider(_ref) {
  var analytics = _ref.analytics,
    children = _ref.children,
    _ref$config = _ref.config,
    config = _ref$config === void 0 ? DEFAULT_CONFIGURATION : _ref$config,
    experiments = _ref.experiments;
  var error = experiments.error,
    _experiments$evaluati = experiments.evaluations,
    initialEvaluations = _experiments$evaluati === void 0 ? {} : _experiments$evaluati,
    _experiments$user = experiments.user,
    user = _experiments$user === void 0 ? {} : _experiments$user;
  var _useState = useState(initialEvaluations),
    evaluations = _useState[0],
    setEvaluations = _useState[1];
  var renderErrors = useRef({});
  // Want to have all config items be explicitly set
  var configWithDefaults = _extends({}, DEFAULT_CONFIGURATION, config);
  var logger = getLogger(configWithDefaults.debugMode);
  var initialExperimentsViewed = {};
  useEffect(function () {
    if (error) {
      logger.error('An error occurred while fetching evaluations.', {
        customAttributes: {
          'experiments.user.anonymousId': user == null ? void 0 : user.anonymousId
        },
        error: error
      });
    } else if (Object.keys(initialEvaluations).length === 0 && configWithDefaults.doEmptyProviderLogging) {
      logger.error('ExperimentsProvider empty initial evaluations', {
        customAttributes: {
          'experiments.user.anonymousId': user == null ? void 0 : user.anonymousId,
          // For authToken, don't see value in logging full auth token when anonymousId available for ID,
          // but knowing if logged in or not on error may be useful so log the boolean
          'experiments.user.hasAuthToken': (user == null ? void 0 : user.authToken) !== undefined
        }
      });
    } else {
      logger.debug('ExperimentsProvider render', {
        initialEvaluations: initialEvaluations,
        user: user
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Thread blocking potential source of increased TBT load times on mobile
  // TODO investigate speedup options
  if (configWithDefaults.doExperimentStashDeduplication) {
    initialExperimentsViewed = getInitialExperimentsViewed({
      serverEvaluations: initialEvaluations,
      user: user,
      debugMode: configWithDefaults.debugMode
    });
  }
  return jsx(ExperimentsViewedProvider, {
    analytics: analytics,
    config: configWithDefaults,
    experimentsViewed: initialExperimentsViewed,
    children: jsx(ExperimentsContext.Provider, {
      value: {
        analytics: analytics,
        config: configWithDefaults,
        evaluations: evaluations,
        logger: logger,
        setEvaluations: setEvaluations,
        user: user != null ? user : {},
        renderErrors: renderErrors
      },
      children: children
    })
  });
};

var useGetEvaluation = function useGetEvaluation(id) {
  if (id === void 0) {
    id = '';
  }
  var _useExperimentsContex = useExperimentsContext(),
    evaluations = _useExperimentsContex.evaluations;
  var evaluation = useMemo(function () {
    return !Object.keys(evaluations).length ? EVALUATION_NO_EVALUATIONS : evaluations[id] || EVALUATION_NOT_FOUND;
  }, [evaluations, id]);
  return evaluation;
};

var getExtraHeaders = function getExtraHeaders(user) {
  var extraHeaders = {};
  if (user.authToken == undefined) {
    var htjsUserId = getCookie(HTJS_USER_ID);
    if (htjsUserId != null) {
      extraHeaders[HTJS_USER_ID] = htjsUserId;
    }
  }
  return extraHeaders;
};

var _excluded = ["user", "req", "logger"];
var initializeExperiments = function initializeExperiments(_ref) {
  var user = _ref.user,
    req = _ref.req,
    _ref$logger = _ref.logger,
    logger = _ref$logger === void 0 ? function (error) {
      return console.error("initializeExperiments FAILED", error);
    } : _ref$logger,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  try {
    if (!req) {
      return Promise.resolve({
        user: user
      });
    }
    return Promise.resolve(getEvaluationsWithOverrides(_extends({
      user: user,
      headers: getExtraHeaders(user),
      req: req
    }, rest))).then(function (payload) {
      if (payload.error) {
        logger(payload.error);
      }
      return payload;
    });
  } catch (e) {
    return Promise.reject(e);
  }
};

var setCookie = function setCookie(key, value, days) {
  var expires = '';
  if (days) {
    var date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = "; expires=" + date.toUTCString();
  }
  try {
    var cookieKey = encodeURIComponent(key);
    var cookieValue = encodeURIComponent(value);
    document.cookie = cookieKey + "=" + cookieValue + expires + "; path=/";
  } catch (e) {
    console.log('error', e);
    /* empty */
  } // TODO log to NR?
};

var UpsertVisualizerContextErrorMsg = function UpsertVisualizerContextErrorMsg(_ref) {
  var error = _ref.error,
    id = _ref.id;
  var _useExperimentsContex = useExperimentsContext(),
    renderErrors = _useExperimentsContex.renderErrors;
  var newRenderErrors = _extends({}, renderErrors.current);
  if (newRenderErrors[id] === undefined) {
    newRenderErrors[id] = [error];
  } else {
    newRenderErrors[id].push(error);
  }
  renderErrors.current = newRenderErrors;
  return null;
};

var ErrorBoundary = /*#__PURE__*/function (_Component) {
  function ErrorBoundary() {
    var _this;
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _Component.call.apply(_Component, [this].concat(args)) || this;
    _this.state = {
      hasError: false,
      error: undefined
    };
    return _this;
  }
  _inheritsLoose(ErrorBoundary, _Component);
  ErrorBoundary.getDerivedStateFromError = function getDerivedStateFromError(error) {
    return {
      hasError: true,
      error: error
    };
  };
  var _proto = ErrorBoundary.prototype;
  _proto.componentDidCatch = function componentDidCatch(error) {
    var _this$props = this.props,
      evaluation = _this$props.evaluation,
      id = _this$props.id,
      logger = _this$props.logger;
    logger.error(ERROR_BOUNDARY_LOG_MSG, {
      customAttributes: {
        'experiment.id': id,
        'experiment.evaluation.reason': evaluation == null ? void 0 : evaluation.reason,
        'experiment.evaluation.variationId': isNaN(evaluation == null ? void 0 : evaluation.variationId) ? null : evaluation == null ? void 0 : evaluation.variationId
      },
      error: error
    });
  };
  _proto.render = function render() {
    var id = this.props.id;
    if (this.state.hasError && this.state.error !== undefined) {
      // returning null since we don't know what to show
      return jsx(UpsertVisualizerContextErrorMsg, {
        error: this.state.error,
        id: id
      });
    }
    return this.props.children;
  };
  return ErrorBoundary;
}(Component);

// Update with WithExperimentConfig, but will be breaking change
var getExcludeUserFromExperiment = function getExcludeUserFromExperiment(excludeUserFromExperimentProps, doExcludeUserCheck) {
  if (doExcludeUserCheck !== undefined) {
    return doExcludeUserCheck(excludeUserFromExperimentProps);
  }
  return false;
};
var withExperiment = function withExperiment(Feature, id, variations, doExcludeUserCheck, doVariantCustomAttributeLog) {
  var WithExperiment = function WithExperiment(featureProps) {
    var _useExperimentsViewed = useExperimentsViewedContext(),
      setExperimentViewed = _useExperimentsViewed.setExperimentViewed;
    var evaluation = useGetEvaluation(id);
    var _useExperimentsContex = useExperimentsContext(),
      logger = _useExperimentsContex.logger;
    var excludeUserFromExperimentProps = featureProps.excludeUserFromExperimentProps;
    var FeatureToRender = !!evaluation.variationId && variations[evaluation.variationId - 1] || Feature;
    useEffect(function () {
      var excludeUserFromExperiment = getExcludeUserFromExperiment(excludeUserFromExperimentProps, doExcludeUserCheck);
      setExperimentViewed({
        experimentId: id,
        evaluation: evaluation,
        exclude: excludeUserFromExperiment
      });
      logger.debug('withExperiment: ', {
        id: id,
        evaluation: evaluation,
        excludeUserFromExperiment: excludeUserFromExperiment
      });
      if (doVariantCustomAttributeLog) {
        // Setting the custom attribute on mount
        // window.NREUM?.setCustomAttribute(id, evaluation.reason);
        setCustomAttribute({
          name: id,
          value: evaluation.variationId
        });
      }
      // Cleanup function runs when the component unmounts
      return function () {
        // Remove the custom attribute here to prevent persisting the attribute
        // during the next page load on a client-side navigation
        if (doVariantCustomAttributeLog) {
          setCustomAttribute({
            name: id,
            value: null
          });
        }
      };
    },
    // Only want to re-render change with excludeUserProps props updating excludeUserFromExperiment
    // Every other variable is just being reported on or should stay constant on component life cycle
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [excludeUserFromExperimentProps]);
    return jsx(ErrorBoundary, {
      id: id,
      evaluation: evaluation,
      logger: logger,
      children: jsx(FeatureToRender, _extends({}, featureProps))
    });
  };
  WithExperiment.displayName = 'WithExperiment';
  return WithExperiment;
};

export { ExperimentsProvider, Stash, addPageAction, getCookie, initializeExperiments, setCookie, useExperimentsContext, useExperimentsViewedContext, useGetEvaluation, withExperiment };
//# sourceMappingURL=index.js.map
